import {
  transform, compact, uniq, flatten, map, omitBy, omit, isNil, isEmpty, pick, get, isEqual, flattenDeep, isNumber
} from 'lodash'
import uuid from 'powtoon-commons/utils/uuid'
import {
  isUserImageItem,
  isTextItem,
  isSoundItem,
  isVideoItem,
  isAvatarItem,
  isSlideBackgroundItem,
  isColorableItem,
  isAnimatedItem,
  isVectorGraphicsItem,
  isCompositeAssetItem,
  isImageHolderItem,
  isItemAllowedInFrame,
  isImageHolderItemWithVideoContent,
  isCharacterItem,
  isFyberItem,
  isVoiceoverItem
} from 'powtoon-document'
import {createNewSlide} from 'powtoon-document/utils'
import {
  ITEM_TYPES,
  EMPTY_POWTOON,
  EMPTY_SLIDE,
  DEFAULT_ANIMATION_LOOPS,
  EMPTY_BACKGROUND
} from 'powtoon-document/consts'
import {allowHandEffect, isTextHandEffect} from 'powtoon-document/utils/enterExitEffects'
import {
  getFirstSprite,
  getSpriteOrSpriteContent,
  isLipSyncAudioSpriteActiveInTrack,
  getLipSyncAudioTrackFromTrack
} from 'powtoon-document/utils/trackSpriteUtils'
import {KEYFRAME_PROPS} from 'powtoon-document/consts/animationGraphs'
import {
  hasAnimationGraphs,
  pixelSizeToPercentageSize,
  percentageSizeToPixelSize,
  alignItemToTopCenter,
  alignItemByCenterPoint
} from 'powtoon-commons/utils'
import {fitItemToFrame} from 'powtoon-commons/utils/fitItemToFrame'
import {
  getAspectRatioSize,
  isCameraValidSizeKeyframes,
  isCameraValidTimeKeyframes,
  isDefaultSizeCamera
} from 'powtoon-commons/utils/cameraAnimationGraphs'
import {createUpdatedManifest, getManifestItemForDocItem} from 'powtoon-document/manifest'
import {roundToSupportedPrecision, calcDocumentDuration} from 'powtoon-document/utils/duration'
import {MINIMUM_STEP_SIZE, MIN_SLIDE_DURATION} from 'powtoon-document/consts/duration'
import {isValidLink, isValidDynamicLink} from 'powtoon-commons/utils/strings'
import {
  isDecimalInteger,
  roundToPrecision
} from 'powtoon-commons/utils/numbers'
import POWTOON_RECOVERY_WARNINGS from './recoverPowtoonWarnings'
import {getAudioDurationFromManifest} from 'powtoon-commons/utils/audio'
import {
  isRichTextData,
  convertRichTextToString,
  isDocTextAnyValue,
  isDocText,
  isRichText,
  isValidTextSpriteStyle,
  getTextSpriteStyleFromRichText
} from 'powtoon-commons/richText/utils'
import {SLIDE_HEIGHT, SLIDE_WIDTH} from 'powtoon-commons/models/consts'
import {getVideoDuration} from 'powtoon-commons/models/utils/mediaObject'
import {
  pushUniqFrontMasterTracks,
  pushUniqBackMasterTracks
} from 'powtoon-commons/models/utils/masterObject'
import {LICENSED_FEATURE_IDS} from 'powtoon-commons/licensing/licensedFeature'
import {colord, getFormat} from 'colord'
import {latestVersion} from '../powtoonContentVersions'
import {applyFunctionOnAllLeafs, DEFAULT_RICH_TEXT_COLOR} from 'powtoon-commons/richText/utils'
import {getVideoWidthHeightFromManifestItem} from '../utils/powtoonValidation'

const isValidAnimationGraphsInTrack = track => {
  if (!hasAnimationGraphs(track)) {
    return true
  }

  const firstKeyframeValues = get(track, 'animationGraphs.keyframes[0].values')
  const spriteValues = pick(track.sprites[0], KEYFRAME_PROPS)
  return isEqual(spriteValues, firstKeyframeValues)
}

const isComposite = track => {
  const sprite = get(track, 'sprites[0]', {})
  return isCompositeAssetItem(sprite)
}

const isValidTrack = track => (
  !!track.id &&
  !!track.sprites &&
  !!track.sprites[0] &&
  !!track.sprites[0].id &&
  !!track.sprites[0].itemType &&
  !isComposite(track)
)

const isValidAudioTrack = audioTrack => (
  audioTrack.itemType === 'sound' &&
  !!audioTrack.id &&
  !!audioTrack.contentItemId &&
  !!audioTrack.audioType &&
  !!audioTrack.enterEffect &&
  !!audioTrack.exitEffect &&
  !!audioTrack.playbackSettings &&
  audioTrack.playbackSettings.endTime >= 0
)

const fixTextTrackText = (track, addWarning) => {
  const sprite = get(track, 'sprites[0]', {})
  if (typeof sprite.text !== 'string') {
    let fixedText
    if (isDocTextAnyValue(sprite) && isRichTextData(sprite.text)) {
      fixedText = convertRichTextToString(sprite.text.value)
    }
    else {
      fixedText = JSON.stringify(sprite.text)
    }
    addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_SPRITE_TEXT, sprite, fixedText})

    return {
      ...track,
      sprites: [{
        ...sprite,
        text: fixedText
      }]
    }
  }

  return track
}

const removeRichTextWithEmptyChildren   = (track) => {
  const sprite = get(track, 'sprites[0]', {})
  
  if (isDocText(sprite) && isRichTextData(sprite.richText)) {
    const value = sprite.richText.value
    if ( value &&  Array.isArray(value)) {
      const filteredValue = value.filter(item => {
        if (isRichText(item)) {
          if (item.children && item.children.length > 0)
          {
            // richText value array , item should not be "type": "paragraph" with empty "children" array
            return true
          }
        }
      })
      return {
        ...track,
        sprites: [{
          ...sprite,
          richText: {
            ... sprite.richText,
            value: filteredValue
          }
        }]
      }
    }
  }

  
  return track
}

const getManifestFontOrDefault = (track, foundFonts, manifest) => {
  const sprite = get(track, 'sprites[0]', {})
  const manifestFont = foundFonts || manifest.fonts[Object.keys(manifest.fonts)[0]]
  const replacementContentItemId = manifestFont?.contentItemId || 'OpenSans'
  const replacementFontName = manifestFont?.name || 'OpenSans'

  return {
    ...track,
    sprites: [{
      ...sprite,
      fontName: replacementFontName,
      contentItemId: replacementContentItemId
    }]
  }
}

const fixTextTrackFontName = (track, manifest) => {
  const sprite = get(track, 'sprites[0]', {})
  const {fontName, contentItemId} = sprite

  if (!fontName) {
    return getManifestFontOrDefault(track, manifest.fonts[contentItemId], manifest)
  }
  return track
}

const fixTextTrackContentItemId = (track, manifest) => {
  const sprite = get(track, 'sprites[0]', {})
  const {fontName, contentItemId} = sprite
  if (!contentItemId) {
    const foundEntries = Object.values(manifest.fonts).find(value => value.name === fontName)
    return getManifestFontOrDefault(track, foundEntries,  manifest)
  }
  return track
}

export const removeTextTrackExtraProps = (track, addWarning) => {
  const extraProps = ['crop']
  const sprite = get(track, 'sprites[0]', {})
  if (extraProps.some((value) => sprite.hasOwnProperty(value))) {
    addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_EXTRA_TEXT_SPRITE_PROPS, sprite})
    return {
      ...track,
      sprites: [{...omit(sprite, extraProps)}]
    }
  }
  return track
}

const fixTextTrack = (track, manifest, addWarning) => {
  let _track = fixTextTrackText(track, addWarning)
  _track = fixTextTrackFontName(_track, manifest)
  _track = fixTextTrackContentItemId(_track, manifest)
  _track = removeTextTrackExtraProps(_track, addWarning)
  _track = removeRichTextWithEmptyChildren(_track)
  return _track
}

const fixTextTrackSpriteData = (track, manifest, addWarning) => {
  const sprite = get(track, 'sprites[0]', {})

  if (isTextItem(sprite)) {
    return fixTextTrack(track, manifest, addWarning)
  }
  const newSprite = {...sprite}
  delete newSprite.fontWeight
  delete newSprite.fontStyle
  delete newSprite.textDecoration

  return {
    ...track,
    sprites: [newSprite]
  }
}

const fixOrRemoveSlideTracks = (slide, slideIndex, addWarning, manifest) => {
  const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
    if (isValidTrack(track)) {

      newTracks.push(fixTextTrackSpriteData(track, manifest, addWarning))
    } else {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_INVALID_TRACK, slideIndex, trackIndex, track})
    }
  }, [])

  return {
    ...slide,
    tracks: newTracks
  }
}

const fixOrRemoveSlideAudioTracks = (slide, slideIndex, addWarning, manifest) => {
  const newAudioTracks = transform(slide.audioTracks, (newAudioTracks, audioTrack, trackIndex) => {
    if (isValidAudioTrack(audioTrack)) {
      const newAudioTrack = {...audioTrack}
      delete newAudioTrack.dataUrl
      delete newAudioTrack.thumbUrl

      newAudioTracks.push(newAudioTrack)
    } else {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_INVALID_AUDIO_TRACK, slideIndex, trackIndex, audioTrack})
    }
  }, [])

  return {
    ...slide,
    audioTracks: newAudioTracks
  }
}

const fixOrRemoveDocumentSlides = (doc, {addWarning, isValidTime, roundTime}) => {
  let newSlides

  if (!doc.slides) {
    addWarning({type: POWTOON_RECOVERY_WARNINGS.BLANK_NO_SLIDES, slides: doc.slides})
    newSlides = EMPTY_POWTOON.slides
  } else {
    newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
      if (!isEmpty(slide) && slide.duration) {
        let newSlide = slide
        if (!isValidTime(slide.duration) || (slide.duration < MIN_SLIDE_DURATION)) {
          newSlide = {...newSlide, duration: Math.max(roundTime(newSlide.duration), MIN_SLIDE_DURATION)}
        }
        newSlide = fixOrRemoveSlideTracks(newSlide, slideIndex, addWarning, doc.manifest)
        newSlide = fixOrRemoveSlideAudioTracks(newSlide, slideIndex, addWarning, doc.manifest)
        newSlides.push(newSlide)
      }
      else {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.BLANK_INVALID_SLIDE, slideIndex, slide})
        const newSlide = createNewSlide()
        newSlides.push(newSlide)
      }
    }, [])
  }

  return {
    ...doc,
    slides: newSlides
  }
}

const sanitizeDocument = (doc, recoverContext) => {
  let docToReturn = doc

  docToReturn = fixOrRemoveDocumentSlides(docToReturn, recoverContext)
  docToReturn = fixDuplicateIdsAndContentItemIdsTypes(docToReturn, recoverContext)

  return docToReturn
}

const fixBrokenFontReferences = (doc, {addWarning}) => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      const firstSprite = getFirstSprite(track)
      const {fontFamily, fontName, ...otherSpriteProps} = firstSprite

      // Due to studio rollback we had cases of old studio working on a document that was already
      // converted to a newer doc version (1.04 → 1.05).
      // This caused the font references in a sprite to be illegal,
      // having both the new (contentItemId + fontName) and old (fontFamily) references.
      // We recognize this from having a sprite with fontName and fontFamily.
      const isBrokenFontSprite = fontFamily && fontName

      // We fix by choosing the fontFamily which is the latest user selection,
      // and adjusting to the new 1.05 document format.
      if (isBrokenFontSprite) {
        addWarning({
          type: POWTOON_RECOVERY_WARNINGS.ADJUST_FONT_REFERENCES, docItem: firstSprite, slideIndex, trackIndex
        })
        newTracks[trackIndex] = {
          ...track,
          sprites: [{
            ...otherSpriteProps,
            contentItemId: fontFamily
          }]
        }
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const fixDuplicateIdsAndContentItemIdsTypes = (doc, {addWarning}) => {
  const {slides} = doc
  const usedKeys = {}

  const newSlides = slides.map((slide, slideIndex) => {
    const newSlide = {...slide}

    if (usedKeys[slide.id]) {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_DUPLICATE_IDS, slideIndex})
      newSlide.id = uuid()
    }
    usedKeys[slide.id] = true

    newSlide.tracks = slide.tracks.map((track, trackIndex) => {
      const newTrack = {...track}

      if (usedKeys[track.id]) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_DUPLICATE_IDS, slideIndex, trackIndex})
        newTrack.id = uuid()
      }
      usedKeys[track.id] = true

      newTrack.sprites = track.sprites.map((sprite, spriteIndex) => {
        const newSprite = {...sprite}

        if (sprite.contentItemId) {
          newSprite.contentItemId = sprite.contentItemId.toString()
        }

        if (usedKeys[sprite.id]) {
          addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_DUPLICATE_IDS, slideIndex, trackIndex, spriteIndex})
          newSprite.id = uuid()
        }
        usedKeys[sprite.id] = true

        return newSprite
      })

      return newTrack
    })

    return newSlide
  })

  return {
    ...doc,
    slides: newSlides
  }
}

const adjustFontNames = (doc, manifest, {addWarning}) => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      const firstSprite = getFirstSprite(track)
      const {contentItemId, fontName} = firstSprite
      const manifestItem = get(manifest, ['fonts', contentItemId], {})
      const manifestFontName = manifestItem.name

      if (fontName !== manifestFontName) {
        addWarning({
          type: POWTOON_RECOVERY_WARNINGS.UPDATE_FONT_NAME, docItem: firstSprite, manifestItem, slideIndex, trackIndex
        })
        const newTrack = {
          ...track,
          sprites: [{
            ...firstSprite,
            fontName: manifestFontName
          }]
        }

        if (!manifestFontName) {
          delete(newTrack.sprites[0].fontName)
        }

        newTracks[trackIndex] = newTrack
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

export const adjustStartEndTimeForTracks = (
  doc, manifest, {addWarning, isValidTime, roundTime, minTimeStep}
) => {
  const getTrackIsValid = (track, {assetLength, playbackSettings, slideDuration}) => {
    const trackDuration = track.endTime - track.startTime
    const playbackSettingsDuration = playbackSettings && (playbackSettings.endTime - playbackSettings.startTime)
    return (
      !playbackSettings || (
        playbackSettingsDuration >= minTimeStep &&
        playbackSettingsDuration <= trackDuration &&
        isValidTime(playbackSettings.startTime) &&
        isValidTime(playbackSettings.endTime) &&
        assetLength >= playbackSettingsDuration
      )) &&
      (track.startTime < track.endTime) &&
      (track.startTime < slideDuration) &&
      (track.startTime >= 0) && (track.endTime > 0) && isValidTime(track.startTime) &&
      (track.endTime <= slideDuration) && isValidTime(track.endTime)
  }

  const getUpdatedStartEndTime = (
    track,
    {slideDuration, minTimeStep, playbackSettingsStartTime, assetLength, slideIndex, trackIndex}
  ) => {
    const warn = type => addWarning({
      type,
      track,
      slideIndex,
      trackIndex,
      slideDuration
    })
    let trackStartTime = roundTime(Math.min(Math.max(track.startTime || 0, 0), slideDuration - minTimeStep))
    let trackEndTime = roundTime(track.endTime)
    let endTime
    let startTime

    if ((playbackSettingsStartTime || playbackSettingsStartTime === 0) && assetLength) {
      startTime = roundTime(Math.min(Math.max(playbackSettingsStartTime, 0), assetLength))
      endTime = roundTime(Math.max(assetLength, startTime + minTimeStep))
      const newDuration = roundTime(Math.max(
        Math.min(endTime - startTime, (slideDuration) - trackStartTime, assetLength), minTimeStep
      ))
      endTime = roundTime(startTime + newDuration)
      trackEndTime = roundTime(trackStartTime + newDuration)
    }

    if (!isValidTime(track.startTime)) {
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_STARTTIME)
    }
    if (!isValidTime(track.endTime)) {
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_STARTTIME)
    }
    
    if (slideDuration < trackEndTime) {
      trackEndTime = roundTime(slideDuration)
      endTime = roundTime(startTime + trackEndTime - trackStartTime)
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_ENDTIME)
    }
    if (trackStartTime === slideDuration) {
      trackStartTime = roundTime(Math.max(slideDuration - minTimeStep, 0))
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_STARTTIME)
    }
    if (trackStartTime < 0) {
      trackStartTime = 0
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_STARTTIME)
    }
    if (trackEndTime < 0) {
      trackEndTime = roundTime(slideDuration)
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_ENDTIME)
    }

    if (trackEndTime < trackStartTime) {
      trackStartTime = roundTime(Math.max(trackEndTime - minTimeStep, 0))
      warn(POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_STARTTIME)
    }

    return {
      startTime: trackStartTime,
      endTime: trackEndTime,
      playbackSettingsStartTime: startTime,
      playbackSettingsEndTime: endTime
    }
  }

  const getUpdatedAudioTrack = (audioTrack, {slideIndex, trackIndex, slideDuration}) => {
    const assetLength = getAudioDurationFromManifest(
      audioTrack.contentItemId,
      manifest,
      roundTime,
      minTimeStep
    )
    const {
      startTime: playbackSettingsStartTime, endTime: playbackSettingsEndTime
    } = audioTrack.playbackSettings || {}

    if (getTrackIsValid(
      audioTrack, {
        assetLength,
        playbackSettings: {startTime: playbackSettingsStartTime, endTime: playbackSettingsEndTime},
        slideDuration
      }
    )) {
      return audioTrack
    }
    const updatedTimeValues = getUpdatedStartEndTime(
      audioTrack,
      {assetLength, playbackSettingsStartTime, minTimeStep, slideDuration, slideIndex, trackIndex}
    )

    addWarning({
      type: POWTOON_RECOVERY_WARNINGS.ADJUST_AUDIO_TRACK_PLAYBACK_SETTINGS_TIME,
      audioTrack,
      slideIndex,
      trackIndex,
      audioTrackDuration: updatedTimeValues.endTime - updatedTimeValues.startTime,
      playbackSettingsStartTime,
      playbackSettingsEndTime,
      soundLength: assetLength
    })

    return  {
      ...audioTrack,
      startTime: updatedTimeValues.startTime,
      endTime: updatedTimeValues.endTime,
      playbackSettings: {
        ...audioTrack.playbackSettings,
        startTime: updatedTimeValues.playbackSettingsStartTime,
        endTime: updatedTimeValues.playbackSettingsEndTime
      }
    }
  }

  const getUpdateTrackLipsync = (track, {slideIndex, trackIndex, slideDuration}) => {
    const sprite = getFirstSprite(track)
    const content = getSpriteOrSpriteContent(track)
    const isAvatar = isAvatarItem(content)
    const {playbackSettings: audioTrackPlaybackSettings, ...lipsyncAudioTrack} = getLipSyncAudioTrackFromTrack(track)
    const assetLength = getAudioDurationFromManifest(
      lipsyncAudioTrack.contentItemId,
      manifest,
      roundTime,
      minTimeStep
    )
    const trackDuration = track.endTime - track.startTime
    const playbackSettings = isAvatar ? content.playbackSettings : audioTrackPlaybackSettings
    const {endTime: playbackEndTime, startTime: playbackStartTime} = playbackSettings || {}

    if (
      getTrackIsValid(track, {slideDuration}) &&
      !(isAvatar && audioTrackPlaybackSettings) &&
      (trackDuration >= lipsyncAudioTrack.endTime && assetLength >= playbackEndTime) &&
      (playbackStartTime === 0 && lipsyncAudioTrack.startTime === 0) &&
      (lipsyncAudioTrack.endTime >= playbackEndTime)
    ) {
      return track
    }
    const updatedTimeValues = getUpdatedStartEndTime(
      track,
      {
        assetLength,
        playbackSettingsStartTime: playbackSettings?.startTime,
        minTimeStep,
        slideDuration,
        slideIndex,
        trackIndex
      }
    )

    addWarning({
      type: POWTOON_RECOVERY_WARNINGS.ADJUST_AUDIO_TRACK_PLAYBACK_SETTINGS_TIME,
      audioTrack: track,
      slideIndex,
      trackIndex,
      audioTrackDuration: updatedTimeValues.endTime - updatedTimeValues.startTime,
      playbackSettingsStartTime: playbackSettings?.startTime,
      playbackSettingsEndTime: playbackSettings?.endTime,
      soundLength: assetLength
    })

    const newPlaybackSettings = {
      ...playbackSettings,
      startTime: 0,
      endTime: roundTime(assetLength)
    }

    const fixedAudioTrack = {
      ...lipsyncAudioTrack,
      startTime: 0,
      endTime: roundTime(Math.min(updatedTimeValues.endTime - updatedTimeValues.startTime, assetLength))
    }

    const newAudioTrack = (
      isAvatar ? fixedAudioTrack : {...fixedAudioTrack, playbackSettings: newPlaybackSettings}
    )

    const newContent = isAvatar ?
      {...content, playbackSettings: newPlaybackSettings, audioTracks: [newAudioTrack]} :
      {...content, audioTracks: [newAudioTrack]}

    const newSprite = isImageHolderItem(sprite) ? {...sprite, content: newContent} : newContent

    return  {
      ...track,
      startTime: updatedTimeValues.startTime,
      endTime: updatedTimeValues.endTime,
      sprites: [newSprite]
    }
  }

  const getUpdatedVideoTrack = (videoTrack, {slideIndex, trackIndex, slideDuration, isInsideFrame = false}) => {
    const sprite = getFirstSprite(videoTrack)
    const assetLength = getVideoDuration(manifest, isInsideFrame ? sprite.content?.contentItemId : sprite.contentItemId)
    const {
      startTime: playbackSettingsStartTime, endTime: playbackSettingsEndTime
    } = (isInsideFrame ? sprite.content?.playbackSettings : sprite.playbackSettings) || {}

    if (getTrackIsValid(
      videoTrack, {
        assetLength,
        playbackSettings: {startTime: playbackSettingsStartTime, endTime: playbackSettingsEndTime},
        slideDuration
      }
    )) {
      return videoTrack
    }

    const updatedTimeValues = getUpdatedStartEndTime(
      videoTrack,
      {assetLength, playbackSettingsStartTime, minTimeStep, slideDuration, slideIndex, trackIndex}
    )

    addWarning({
      type: POWTOON_RECOVERY_WARNINGS.ADJUST_VIDEO_TRACK_SETTINGS_TIME,
      videoTrack,
      slideIndex,
      trackIndex,
      trackDuration: videoTrack.endTime - videoTrack.startTime,
      playbackSettingsStartTime,
      playbackSettingsEndTime,
      soundLength: assetLength
    })

    return {
      ...videoTrack,
      startTime: updatedTimeValues.startTime,
      endTime: updatedTimeValues.endTime,
      sprites: [{
        ...sprite,
        playbackSettings: {
          ...sprite.playbackSettings,
          startTime: updatedTimeValues.startTime,
          endTime: updatedTimeValues.endTime
        }
      }]
    }
  }

  const getUpdatedTrack = (track, {slideIndex, trackIndex, slideDuration}) => {
    const sprite = getFirstSprite(track)
    const isVideoHolder = isImageHolderItemWithVideoContent(sprite)
    const isLipsyncInsideTrack = isLipSyncAudioSpriteActiveInTrack(track)
    const isTrackContainsAnotherAsset = isVideoHolder || isLipsyncInsideTrack
    const newTimes = {startTime: roundTime(track.startTime), endTime: roundTime(track.endTime)}
    const additionalContent = {}

    if (isTrackContainsAnotherAsset) {
      if (isVideoHolder) {
        const videoTrack = getUpdatedVideoTrack(track, {slideIndex, trackIndex, slideDuration, isInsideFrame: true})
        if (isEqual(track, videoTrack)) {
          return track
        }
        const {startTime, endTime} = videoTrack
        const {playbackSettings: {
          startTime: playbackSettingsStartTime, endTime: playbackSettingsEndTime
        }} = getFirstSprite(videoTrack)
        newTimes.startTime = startTime
        newTimes.endTime = endTime
        additionalContent.content = {
          ...sprite.content,
          playbackSettings: {
            ...sprite.content.playbackSettings,
            startTime: playbackSettingsStartTime,
            endTime: playbackSettingsEndTime
          }
        }
      }
      if (isLipsyncInsideTrack) {
        const lipsyncTrack = getUpdateTrackLipsync(track, {slideIndex, trackIndex, slideDuration})
        return isEqual(track, lipsyncTrack) ? track : lipsyncTrack
      }
    } else {
      if (getTrackIsValid(
        track, {
          assetLength: null,
          playbackSettings: null,
          slideDuration
        }
      )) {
        return track
      }

      const {startTime, endTime} = getUpdatedStartEndTime(track, {
        assetLength: null,
        playbackSettings: null,
        slideDuration, minTimeStep, slideIndex, trackIndex
      })

      newTimes.startTime = startTime
      newTimes.endTime = endTime
    }

    return {
      ...track,
      ...newTimes,
      sprites: [{
        ...sprite,
        ...additionalContent
      }]
    }
  }

  const getUpdatedTracks = (tracks, slideDuration, slideIndex) => {
    return transform(tracks, (newTracks, track, trackIndex) => {
      const sprite = getFirstSprite(track)
      const isAudioTrack = isSoundItem(track)
      const isVideoTrack = isVideoItem(sprite)
      if (isAudioTrack) {
        newTracks[trackIndex] = getUpdatedAudioTrack(track, {slideIndex, trackIndex, slideDuration})
      } else if (isVideoTrack) {
        newTracks[trackIndex] = getUpdatedVideoTrack(track, {slideIndex, trackIndex, slideDuration})
      } else {
        newTracks[trackIndex] = getUpdatedTrack(track, {slideIndex, trackIndex, slideDuration})
      }
      return newTracks
    }, [])
  }

  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const updatedAudioTracks = getUpdatedTracks(slide.audioTracks, slide.duration, slideIndex)
    const updatedTracks = getUpdatedTracks(slide.tracks, slide.duration, slideIndex)

    newSlides[slideIndex] = {
      ...slide,
      audioTracks: updatedAudioTracks,
      tracks: updatedTracks
    }

    return newSlides
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const adjustTrackAnimationGraphs = (doc, {addWarning}) => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      if (!isValidAnimationGraphsInTrack(track)) {
        const firstKeyframeValues = get(track, 'animationGraphs.keyframes[0].values', {})
        const newSprite = {
          ...getFirstSprite(track),
          ...firstKeyframeValues
        }
        newTracks[trackIndex] = {
          ...track,
          sprites: [newSprite]
        }
        addWarning({track, slideIndex, trackIndex, type: POWTOON_RECOVERY_WARNINGS.ADJUST_TRACK_ANIMATION_GRAPHS})
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const removeTrackExtraProps = (doc, {addWarning}) => {
  const extraProps = ['bouncing', 'animating', 'selectedKeyframeIndex']
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      if (extraProps.reduce((prev, curr) => prev || track.hasOwnProperty(curr), false)) {
        newTracks[trackIndex] = {...omit(track, extraProps)}
        addWarning({track, slideIndex, trackIndex, type: POWTOON_RECOVERY_WARNINGS.REMOVE_EXTRA_TRACK_PROPS})
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const findInManifest = (docItemOrAssetId, manifest) => {
  if (!docItemOrAssetId) {
    return docItemOrAssetId
  }

  if (typeof docItemOrAssetId === 'string') {
    return manifest.assets[docItemOrAssetId]
  }

  if (isTextItem(docItemOrAssetId)) {
    return manifest.fonts[docItemOrAssetId.contentItemId]
  }
  else if (isUserImageItem(docItemOrAssetId)) {
    return manifest.images[docItemOrAssetId.contentItemId]
  }
  else if (isVideoItem(docItemOrAssetId)) {
    return manifest.videos[docItemOrAssetId.contentItemId] || manifest.assets[docItemOrAssetId.contentItemId]
  }
  else if (isSoundItem(docItemOrAssetId)) {
    return manifest.sounds[docItemOrAssetId.contentItemId]
  }
  else if (docItemOrAssetId.contentItemId) {
    return manifest.assets[docItemOrAssetId.contentItemId]
  }

  return null
}

const adjustDocItemToManifestItem = (docItem, manifest, addWarning) => {
  if (isTextItem(docItem)) {
    return docItem
  }

  const manifestItem = findInManifest(docItem, manifest)
  if (!manifestItem || manifestItem.itemType === docItem.itemType) {
    return docItem
  }

  const newDocItem = omitBy({...docItem, itemType: manifestItem.itemType, assetType: manifestItem.assetType}, isNil)
  if (isVectorGraphicsItem(newDocItem) && newDocItem.playbackSettings) {
    delete newDocItem.playbackSettings
  } else if (isAnimatedItem(newDocItem) && !newDocItem.playbackSettings) {
    newDocItem.playbackSettings = {loopBehavior: 'counted', numberOfLoops: DEFAULT_ANIMATION_LOOPS}
  }
  addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_ITEM_TYPE, docItem, manifestItem, newDocItem})

  return newDocItem
}

const adjustItemTypeAndAssetType = (doc, manifest, {addWarning}) => {
  const newAudioTracks = transform(doc.audioTracks, (newAudioTracks, audioTrack, index) => {
    const manifestItem = findInManifest(audioTrack, manifest)
    if (manifestItem && manifestItem.assetType !== audioTrack.assetType) {
      const newAudioTrack = omitBy({...audioTrack, assetType: manifestItem.assetType}, isNil)
      addWarning({
        type: POWTOON_RECOVERY_WARNINGS.ADJUST_ASSET_TYPE, docItem: audioTrack, manifestItem, newDocItem: newAudioTrack
      })
      newAudioTracks[index] = newAudioTrack
    } else {
      newAudioTracks[index] = audioTrack
    }
  }, [])

  const newSlides = transform(doc.slides, (newSlides, slide, index) => {
    const newTracks = transform(slide.tracks, (newTracks, track, index) => {
      const newSprites = transform(track.sprites, (newSprites, sprite, index) => {
        newSprites[index] = adjustDocItemToManifestItem(sprite, manifest, addWarning)
        if (sprite.content && sprite.content.contentItemId) {
          newSprites[index].content = adjustDocItemToManifestItem(sprite.content, manifest, addWarning)
        }
      }, [])
      newTracks[index] = {...track, sprites: newSprites}
    }, [])
    newSlides[index] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    audioTracks: newAudioTracks,
    slides: newSlides
  }
}

const removeMissingAudio = (audioTracks, manifest, addWarning, sourceType) =>
  transform(audioTracks, (newAudioTracks, audioTrack, index) => {
    const sourceToWaringTypesMapping = {
      document: POWTOON_RECOVERY_WARNINGS.REMOVE_DOCUMENT_AUDIO_TRACK,
      slide: POWTOON_RECOVERY_WARNINGS.REMOVE_SLIDE_AUDIO_TRACK
    }
    if (audioTrack === null) {
      newAudioTracks[index] = null
    }
    else if (findInManifest(audioTrack, manifest) === undefined) {
      addWarning({type: sourceToWaringTypesMapping[sourceType], audioTrack})
      newAudioTracks[index] = null
    }
    else {
      newAudioTracks[index] = {...audioTrack}
      if (audioTrack.licenseSource && findInManifest(audioTrack.licenseSource, manifest) === undefined) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_LICENSE_SOURCE, audioTrack})
        delete(newAudioTracks[index].licenseSource)
      }
    }
  }, [])

const removeMissingVideoBackgroundAndAudio = (doc, manifest, {addWarning}) => {

  const newAudioTracks = removeMissingAudio(doc.audioTracks, manifest, addWarning, 'document')

  const newSlides = doc.slides.map(slide => {
    if ((isVideoItem(slide.background) && findInManifest(slide.background, manifest) === undefined)
     || !isSlideBackgroundItem(slide.background)) {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_BACKGROUND, background: slide.background})
      slide.background = EMPTY_SLIDE.background
    }
    const newSlideAudioTracks = removeMissingAudio(slide.audioTracks, manifest, addWarning, 'slide')
    slide.audioTracks = compact(newSlideAudioTracks)
    return slide
  })

  return {
    ...doc,
    audioTracks: newAudioTracks.length ? newAudioTracks : [null, null],
    slides: newSlides
  }
}

export const adjustEnterExitEffects = (doc, {addWarning}) => {

  const removeInvalidHandEffect = (track, effectType, warningParams) => {
    addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_INVALID_HAND_EFFECT, track, effectType, ...warningParams})
    return {
      ...track,
      [effectType]: {
        ...track[effectType],
        handContentId: null
      }
    }
  }

  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      const {enterEffect, exitEffect} = track

      if (enterEffect.handContentId && !isTextHandEffect(enterEffect) && !allowHandEffect(enterEffect.effectId)) {
        track = removeInvalidHandEffect(track, 'enterEffect', {slideIndex, trackIndex})
      }

      if (exitEffect.handContentId && !allowHandEffect(exitEffect.effectId)) {
        track = removeInvalidHandEffect(track, 'exitEffect', {slideIndex, trackIndex})
      }

      newTracks[trackIndex] = track
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const removeInvalidLinks = (doc, {addWarning}) => {

  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      if (!isValidLink(track.link) && !isValidDynamicLink(track.link)) {
        newTracks[trackIndex] = omit(track, 'link')
        addWarning({track, slideIndex, trackIndex, type: POWTOON_RECOVERY_WARNINGS.REMOVE_INVALID_TRACK_LINK})
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const adjustStartEndVideoBackground = (doc, {addWarning}) => {

  const newSlides = doc.slides.map((slide, slideIndex) => {
    const {duration: slideDuration, background: {itemType, playbackSettings} = {}} = slide

    if (itemType === ITEM_TYPES.VIDEO && playbackSettings) {
      const {endTime, startTime} = playbackSettings
      if (endTime < startTime) {
        addWarning({
          type: POWTOON_RECOVERY_WARNINGS.ADJUST_SLIDE_VIDEO_BACKGROUND_TIME, slideIndex, background: slide.background
        })
        return {
          ...slide,
          background: {
            ...slide.background,
            playbackSettings: {...playbackSettings, startTime: endTime}
          }
        }
      }

      if ((endTime - startTime) > slideDuration) {
        addWarning({
          type: POWTOON_RECOVERY_WARNINGS.ADJUST_SLIDE_VIDEO_BACKGROUND_TIME, slideIndex, slideDuration,
          background: slide.background
        })
        return {
          ...slide,
          background: {
            ...slide.background,
            playbackSettings: {...playbackSettings, endTime: startTime + slideDuration}
          }
        }
      }
    }
    return slide
  })

  return {
    ...doc,
    slides: newSlides
  }
}

const adjustDocumentAudioTracks = (doc) => {
  const documentDuration = calcDocumentDuration(doc.slides)
  return {
    ...doc,
    audioTracks: doc.audioTracks.map(audioTrack => {
      if (!audioTrack?.itemType || !audioTrack?.contentItemId) {
        return null
      }

      if (!isNumber(audioTrack.startTime) || !isNumber(audioTrack.endTime)) {
        const {length} = getManifestItemForDocItem(doc.manifest, audioTrack)
        const isVoiceover = isVoiceoverItem(audioTrack)

        return {
          ...audioTrack,
          startTime: audioTrack.startTime ?? 0,
          endTime: audioTrack.endTime ?? (
            isVoiceover ? Math.min(roundToSupportedPrecision(length), documentDuration) : documentDuration)
        }
      }

      return audioTrack
    })
  }
}

const recreateDocumentWithoutMissingManifestRefs = (doc, manifest, {addWarning}) => {

  const newDoc = {...doc}

  if (doc.templateId && findInManifest(doc.templateId, manifest) === undefined) {
    addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_TEMPLATE_ID, templateId: doc.templateId})
    delete(newDoc.templateId)
  }

  newDoc.slides = []
  transform(doc.slides, (newSlides, slide, slideIndex) => {
    newSlides[slideIndex] = {...slide}
    if (slide.sceneId && findInManifest(slide.sceneId, manifest) === undefined) {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_SCENE_ID, slide})
      delete(newSlides[slideIndex].sceneId)
    }

    if (slide.background.contentItemId && findInManifest(slide.background, manifest) === undefined) {
      addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_BACKGROUND, background: slide.background})
      newSlides[slideIndex].background = {...EMPTY_BACKGROUND}
    } else {
      newSlides[slideIndex] = {...slide}
      if (slide.background.licenseSource && findInManifest(slide.background.licenseSource, manifest) === undefined) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_LICENSE_SOURCE, docItem: slide.background})
        delete(newSlides[slideIndex].background.licenseSource)
      }
    }

    const newTracks = []
    transform(slide.tracks, (newTracks, track, trackIndex) => {
      if (findInManifest(getFirstSprite(track), manifest) === undefined) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_SPRITE, sprite: getFirstSprite(track)})
        newTracks[trackIndex] = null
      } else {
        newTracks[trackIndex] = {...track}
        if (getFirstSprite(track).licenseSource
          && findInManifest(getFirstSprite(track).licenseSource, manifest) === undefined) {
          addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_LICENSE_SOURCE, docItem: getFirstSprite(track)})
          delete(getFirstSprite(newTracks[trackIndex]).licenseSource)
        }
        if (getFirstSprite(track).content) {
          if (findInManifest(getFirstSprite(track).content, manifest) === undefined) {
            addWarning({type: POWTOON_RECOVERY_WARNINGS.REMOVE_SPRITE_CONTENT, content: getFirstSprite(track).content})
            getFirstSprite(newTracks[trackIndex]).content = {}
          } else {
            getFirstSprite(newTracks[trackIndex]).content = {...getFirstSprite(track).content}
            if (getFirstSprite(track).content.licenseSource
              && findInManifest(getFirstSprite(track).content.licenseSource, manifest) === undefined) {
              addWarning({
                type: POWTOON_RECOVERY_WARNINGS.REMOVE_LICENSE_SOURCE, docItem: getFirstSprite(track).content
              })
              delete(getFirstSprite(newTracks[trackIndex]).content.licenseSource)
            }
          }
        }
      }
    }, newTracks)
    newSlides[slideIndex].tracks = compact(newTracks)
  }, newDoc.slides)

  return newDoc
}

const addDocItemToServerManifest = (docItem, newManifest) => {
  if (!docItem) {
    return
  }

  if (isTextItem(docItem)) {
    newManifest.fonts.push(docItem.contentItemId)
  }
  else if (isUserImageItem(docItem)) {
    newManifest.images.push(docItem.contentItemId)
  }
  else if (isVideoItem(docItem)) {
    newManifest.videos.push(docItem.contentItemId)
  }
  else if (isSoundItem(docItem)) {
    newManifest.sounds.push(docItem.contentItemId)
  }
  else if (docItem.contentItemId) {
    newManifest.assets.push(docItem.contentItemId)
  }

  if (docItem.licenseSource) {
    newManifest.assets.push(docItem.licenseSource)
  }

  if (docItem.enterEffect && docItem.enterEffect.soundEffectId) {
    newManifest.sounds.push(docItem.enterEffect.soundEffectId)
  }
  if (docItem.exitEffect && docItem.exitEffect.soundEffectId) {
    newManifest.sounds.push(docItem.exitEffect.soundEffectId)
  }
  
  if (docItem.featureId) {
    newManifest.assets.push(docItem.featureId)
  }
}

const addTracksToServerManifest = (tracks, manifestObject) => {
  if (!tracks) {
    return
  }
  transform(tracks, (manifestObject, track) => {
    addDocItemToServerManifest(track, manifestObject)
    addDocItemToServerManifest(getFirstSprite(track), manifestObject)
    addDocItemToServerManifest(getFirstSprite(track).content, manifestObject)
  }, manifestObject)
}

export const regenerateManifestForServerFromDoc = (doc) => {
  const newManifest = {assets: [], images: [], videos: [], fonts: [], sounds: []}

  if (doc.templateId) {
    newManifest.assets.push(doc.templateId)
  }

  if (doc.slides.some(slide => slide.camera?.featureId)) {
    newManifest.assets.push(LICENSED_FEATURE_IDS.CAMERA)
  }

  const spritesAudioTracks = flattenDeep(doc.slides.map(slide => (
    compact(slide.tracks.map(track => {
      const sprite = getFirstSprite(track)
      if (!isEmpty(sprite.audioTracks)) {
        return sprite.audioTracks
      }
    }))
  )))

  const slidesAudioTracks = flatten(map(doc.slides, 'audioTracks'))
  const powtoonAudioTracks = doc.audioTracks

  const audioTracks = [...powtoonAudioTracks, ...slidesAudioTracks, ...spritesAudioTracks]

  transform(audioTracks, (newManifest, audioTrack) => {
    addDocItemToServerManifest(audioTrack, newManifest)
  }, newManifest)

  transform(doc.slides, (newManifest, slide) => {
    if (slide.sceneId) {
      newManifest.assets.push(slide.sceneId)
    }

    addDocItemToServerManifest(slide.background, newManifest)

    addTracksToServerManifest(slide.tracks, newManifest)

    addTracksToServerManifest(slide.captions, newManifest)
  }, newManifest)
  
  addTracksToServerManifest(doc.masters?.tracks?.back, newManifest)
  addTracksToServerManifest(doc.masters?.tracks?.front, newManifest)

  return uniqServerManifest(newManifest)
}

const sanitizeSlideBackground = doc => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    
    const {blurLevel, ...background} = slide.background

    if (background.itemType !== 'color' && blurLevel) {
      background.blurLevel = blurLevel
    }
    if (background.itemType === 'color'  ) {
      if (colord(background.color).isValid() && getFormat(background.color) !== 'hex') {
        background.color = colord(background.color).toHex()
      }
    }

    newSlides[slideIndex] = {...slide, background}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

const uniqServerManifest = manifest =>
  transform(manifest, (uniqMan, val, key) => uniqMan[key] = uniq(val), {})

const adjustSpriteToPositiveSize = (doc, {addWarning}) => {
  const newSlides = map(doc.slides, (slide, slideIndex) => {
    const newTracks = map(slide.tracks, (track, trackIndex) => {
      const sprite = getFirstSprite(track)
      const {width, height} = sprite

      if (width < 0 || height < 0) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_SPRITE_SIZE, sprite})
        return {...track, sprites: [{...sprite, height: Math.abs(height), width: Math.abs(width)}]}
      }

      return track
    })

    return {...slide, tracks: newTracks}
  })

  return {...doc, slides: newSlides}
}

const adjustInvalidSlideCamera = (doc, {addWarning, isValidTime, roundTime, minTimeStep}) => {

  const newSlides = map(doc.slides, (slide) => {
    const {multiplier} = getAspectRatioSize(doc.exportAspectRatio?.method)

    if (!slide.camera || isEmpty(slide.camera)) {
      return slide
    }

    const cameraStaticProperties = {
      featureId: LICENSED_FEATURE_IDS.CAMERA
    }

    if (slide.camera.animationGraphs) {
      let newTimes
      if (!isCameraValidTimeKeyframes(slide.camera.animationGraphs.keyframes, slide.duration, isValidTime)) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_CAMERA_TIME, camera: slide.camera})
        newTimes = [
          roundTime(slide.camera.animationGraphs.keyframes[0]?.time || 0),
          roundTime(slide.camera.animationGraphs.keyframes[1]?.time ||
            (slide.camera.animationGraphs.keyframes[0].time + (minTimeStep * 2)))
        ]
        let timeDuration = roundTime(newTimes[1] - newTimes[0])
        if (timeDuration < minTimeStep * 2) {
          timeDuration = minTimeStep * 2
        }
        else if (timeDuration > slide.duration) {
          timeDuration = slide.duration
        }
        newTimes[0] = Math.min(Math.max(newTimes[0], 0), roundTime(slide.duration - (minTimeStep * 2)))
        newTimes[1] = Math.min(roundTime(newTimes[0] + timeDuration), slide.duration)
      }

      const isNotValidValues = !isCameraValidSizeKeyframes(slide.camera.animationGraphs.keyframes)
      if (isNotValidValues) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_CAMERA_SIZE, camera: slide.camera})
      }
      if (isNotValidValues || newTimes) {
        const keyframes = slide.camera.animationGraphs.keyframes.map((keyframe, index) => {
          const values = {...keyframe.values}
          if (isNotValidValues) {
            if (values.height > SLIDE_HEIGHT || values.height <= 0) {
              values.height = SLIDE_HEIGHT
            }
            values.width = Math.round(values.height / multiplier)
            if (values.x < 0) {
              values.x = 0
            }
            else if (values.x + values.width > SLIDE_WIDTH) {
              values.x = SLIDE_WIDTH - values.width
            }
            if (values.y < 0) {
              values.y = 0
            }
            else if (values.y + values.height > SLIDE_HEIGHT) {
              values.y = SLIDE_HEIGHT - values.height
            }
          }
          return {...keyframe, values, time: newTimes ? newTimes[index] : keyframe.time}
        })
        const newSlide = {...slide, camera: {animationGraphs: {keyframes}, ...cameraStaticProperties}}
        if (isDefaultSizeCamera(newSlide.camera, doc.exportAspectRatio?.method)) {
          delete newSlide.camera
        }
        return newSlide
      }
      else if (isDefaultSizeCamera(slide.camera, doc.exportAspectRatio?.method)) {
        addWarning({type: POWTOON_RECOVERY_WARNINGS.ADJUST_CAMERA_SIZE, camera: slide.camera})
        const newSlide = {...slide}
        delete newSlide.camera
        return newSlide
      }
    }
    return {...slide, camera: {...slide.camera, ...cameraStaticProperties}}
  })
  return {...doc, slides: newSlides}
}

const adjustExportAspectRatio = (doc, {addWarning}) => {
  if (isNil(doc.exportAspectRatio)) {
    addWarning({type: POWTOON_RECOVERY_WARNINGS.MISSING_EXPORT_ASPECT_RATIO})
    return {...doc, exportAspectRatio: {method: 'none'}}
  }
  return doc
}

export const adjustSpriteDuration = (doc) => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      const firstSprite = getFirstSprite(track)
      if (!isVideoItem(firstSprite)) {
        return newTracks[trackIndex] = track
      }
      const trackDuration = track.endTime - track.startTime
      const {playbackSettings} = firstSprite
      const playbackSettingsDuration = playbackSettings.endTime - playbackSettings.startTime
      if (trackDuration !== playbackSettingsDuration) {
        const newTrack = {
          ...track,
          sprites: [{
            ...firstSprite,
            playbackSettings: {
              ...playbackSettings,
              startTime: 0,
              endTime: trackDuration
            }
          }]
        }

        newTracks[trackIndex] = newTrack
      } else {
        newTracks[trackIndex] = track
      }
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

export const adjustSpriteColors = (doc) => {
  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transform(slide.tracks, (newTracks, track, trackIndex) => {
      const firstSprite = getFirstSprite(track)
      if (!isColorableItem(firstSprite) || !firstSprite.colors ) {
        return newTracks[trackIndex] = track
      }
      const newColors = transform(firstSprite.colors, (newColors, color, colorIndex) => {
        if (colord(color).isValid() && getFormat(color) !== 'hex') {
          newColors[colorIndex] = colord(color).toHex()
        }
        else {
          newColors[colorIndex] = color
        }
      }, [])

      firstSprite.colors = newColors

      const newTrack = {
        ...track,
        sprites: [{
          ...firstSprite,
          colors: newColors
        }]
      }
      newTracks[trackIndex] = newTrack
    }, [])
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

export const adjustSpriteRichText = (doc) => {
  const transformTracks = (tracks) => {
    if (!tracks) {
      return []
    }
    return transform(tracks, (newTracks, track, trackIndex) => {
      let firstSprite = {...getFirstSprite(track)}
      if (firstSprite?.richText?.value) {
        applyFunctionOnAllLeafs(firstSprite.richText.value, (leaf) => {
          if (leaf.hasOwnProperty('background_color')) {
            leaf.backgroundColor = leaf.background_color
            delete leaf.background_color
          }
          if (!leaf.color || !colord(leaf.color).isValid()) {
            leaf.color = DEFAULT_RICH_TEXT_COLOR
          }
          else if (colord(leaf.color).isValid() && getFormat(leaf.color) !== 'hex') {
            leaf.color = colord(leaf.color).toHex()
          }
        })
        if (!isValidTextSpriteStyle(firstSprite)) {
          const textStyle = getTextSpriteStyleFromRichText(firstSprite.richText.value)
          firstSprite = {...firstSprite, ...textStyle}
        }
        const newTrack = {
          ...track,
          sprites: [{
            ...firstSprite
          }]
        }
        newTracks[trackIndex] = newTrack
      }
      else {
        newTracks[trackIndex] = track
      }
    }, [])
  }

  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transformTracks(slide.tracks)
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])

  const newMasters = {
    tracks: {
      front: transformTracks(doc.masters?.tracks?.front),
      back: transformTracks(doc.masters?.tracks?.back)
    }
  }

  return {
    ...doc,
    slides: newSlides,
    masters: newMasters
  }
}

const fixManifestSoundsLength = manifest => (
  {
    ...manifest,
    sounds: transform(manifest.sounds,
      (newSounds, sound, key) => (
        newSounds[key] = (isNil(sound.length) || sound.length < 0) ? {...sound, length: 0} : sound
      ),
      {})
  }
)

export const adjustImageHolderContent = (doc) => {
  const newSlides = transform(doc.slides, (newSlides, slide, index) => {
    const newTracks = transform(slide.tracks, (newTracks, track, index) => {
      const newSprites = track.sprites.map(sprite => {
        if (!isImageHolderItem(sprite)) {
          return sprite
        }

        const spriteContent = sprite.content

        if (!isItemAllowedInFrame(spriteContent)) {
          return {...sprite, content: {}}
        }

        let adjustedSprite = sprite

        if (!sprite.content.id) {
          adjustedSprite = {
            ...adjustedSprite,
            content: {
              ...adjustedSprite.content,
              id: uuid()
            }
          }
        }

        const pixelMaskOffset = percentageSizeToPixelSize(spriteContent, sprite)
        const manifestItem = findInManifest(spriteContent, doc.manifest)
        const {width: originalWidth, height: originalHeight} = getVideoWidthHeightFromManifestItem(manifestItem)
        const currentAspectRatio = pixelMaskOffset.width / pixelMaskOffset.height
        const expectedAspectRatio = originalWidth / originalHeight
        const isDifferentAspectRatio = expectedAspectRatio &&
            (roundToPrecision(currentAspectRatio - expectedAspectRatio, 1) !== 0)

        if (isDifferentAspectRatio) {
          const fittedImage = fitItemToFrame({width: originalWidth, height: originalHeight}, pixelMaskOffset)
          const alignImage = () => {
            if (isAvatarItem(spriteContent)) {
              return alignItemToTopCenter(fittedImage, pixelMaskOffset)
            }
            return alignItemByCenterPoint(fittedImage, pixelMaskOffset)
          }
        
          const alignedImage = alignImage()
          
          const resultAlignedImage = pixelSizeToPercentageSize(alignedImage, sprite)

          adjustedSprite = {
            ...adjustedSprite,
            content: {
              ...adjustedSprite.content,
              ...pick(resultAlignedImage, 'x', 'y', 'width', 'height')
            }
          }
        }

        return adjustedSprite
      })
      newTracks[index] = {...track, sprites: newSprites}
    }, [])
    newSlides[index] = {...slide, tracks: newTracks}
  }, [])

  return {
    ...doc,
    slides: newSlides
  }
}

export const removeMasterTracksDuplicates = (doc) => {
  const masterTracks = {
    front: [],
    back: []
  }

  if (!doc.masters?.tracks) {
    return {...doc}
  }

  pushUniqFrontMasterTracks(doc.masters.tracks.front || [], masterTracks)
  pushUniqBackMasterTracks(doc.masters.tracks.back || [], masterTracks)

  return {
    ...doc,
    masters: {
      tracks: masterTracks
    }
  }

}

export const adjustCharacterSprites = (doc) => {
  const transformTracks = (tracks) => {
    if (!tracks) {
      return []
    }
    return transform(tracks, (newTracks, track, trackIndex) => {
      const firstSprite = {...getFirstSprite(track)}
      if (isCharacterItem(firstSprite) && !isFyberItem(firstSprite)) {
        if (firstSprite.expression) {
          delete firstSprite.expression
        }
        newTracks[trackIndex] = {...track, sprites: [firstSprite]}
        return
      }
      newTracks[trackIndex] = track
    }, [])
  }

  const newSlides = transform(doc.slides, (newSlides, slide, slideIndex) => {
    const newTracks = transformTracks(slide.tracks)
    newSlides[slideIndex] = {...slide, tracks: newTracks}
  }, [])
  return {...doc, slides: newSlides}
}

export const recoverPowtoonContentPromise = (docId, content, {
  removeContent = false,
  expandManifestService,
  logger = console,
  roundTime = roundToSupportedPrecision,
  isValidTime = isDecimalInteger,
  minTimeStep = MINIMUM_STEP_SIZE,
  skipVersionCheck = false
} = {}, additionalInfo) => {
  if (content.version !== latestVersion && !skipVersionCheck) {
    const e = new Error('Recovery is applicable to latest document version only...')
    logger.error(e, {actualLatestVersion: latestVersion, receivedVersion: content.version})
    return Promise.reject(e)
  }

  const warnings = []
  const addWarning = warning => warnings.push(warning)
  const recoverContext = {minTimeStep, roundTime, isValidTime, addWarning}

  let powtoonToReturn = content.powtoon
  powtoonToReturn = sanitizeDocument(powtoonToReturn, recoverContext)

  powtoonToReturn = fixBrokenFontReferences(powtoonToReturn, recoverContext)

  powtoonToReturn = adjustInvalidSlideCamera(powtoonToReturn, recoverContext)

  const manifestForServer = regenerateManifestForServerFromDoc(powtoonToReturn)
  const manifestFromServerPromise = expandManifestService(docId, manifestForServer, additionalInfo)

  return manifestFromServerPromise.then(({manifest}) => {

    //extend new manifest with old one, so we will get entire original manifest + missed occurrences from new one
    manifest = manifest ? createUpdatedManifest(powtoonToReturn.manifest, manifest) : powtoonToReturn.manifest

    manifest = fixManifestSoundsLength(manifest)

    if (removeContent) {
      powtoonToReturn = recreateDocumentWithoutMissingManifestRefs(powtoonToReturn, manifest, recoverContext)
    }
    else {
      // Running recreateDocumentWithoutMissingManifestRefs even when removeContent is false, to collect warnings
      recreateDocumentWithoutMissingManifestRefs(powtoonToReturn, manifest, recoverContext)
    }

    powtoonToReturn = adjustItemTypeAndAssetType(powtoonToReturn, manifest, recoverContext)

    powtoonToReturn = removeMissingVideoBackgroundAndAudio(powtoonToReturn, manifest, recoverContext)

    powtoonToReturn = adjustFontNames(powtoonToReturn, manifest, recoverContext)

    powtoonToReturn = adjustTrackAnimationGraphs(powtoonToReturn, recoverContext)

    powtoonToReturn = removeTrackExtraProps(powtoonToReturn, recoverContext)

    powtoonToReturn = adjustEnterExitEffects(powtoonToReturn, recoverContext)

    powtoonToReturn = adjustStartEndVideoBackground(powtoonToReturn, recoverContext)

    powtoonToReturn = adjustStartEndTimeForTracks(powtoonToReturn, manifest, recoverContext)

    powtoonToReturn = removeInvalidLinks(powtoonToReturn, recoverContext)

    powtoonToReturn = sanitizeSlideBackground(powtoonToReturn)

    powtoonToReturn = adjustSpriteToPositiveSize(powtoonToReturn, recoverContext)

    powtoonToReturn = adjustExportAspectRatio(powtoonToReturn, recoverContext)

    powtoonToReturn = adjustSpriteDuration(powtoonToReturn)

    powtoonToReturn = adjustImageHolderContent(powtoonToReturn)

    powtoonToReturn = removeMasterTracksDuplicates(powtoonToReturn)

    powtoonToReturn = adjustSpriteColors(powtoonToReturn)

    powtoonToReturn = adjustSpriteRichText(powtoonToReturn)

    powtoonToReturn = adjustCharacterSprites(powtoonToReturn)

    powtoonToReturn = adjustDocumentAudioTracks(powtoonToReturn)

    return {recoveredContent: {...content, powtoon: {...powtoonToReturn, manifest}}, warnings}
  }).catch(e => {
    logger.error(e)
    throw e
  })
}
